/**
 * core.ts -- data model and conventions.
 *
 * Direct port of `backtest/core.py`. Kept module-for-module and
 * name-for-name with the Python so that a drift between the two is visible in a
 * diff rather than buried in a restructure.
 *
 * Conventions (identical to the Python; see METHODOLOGY.md §2):
 *   rates/strikes   absolute decimals, 0.025 = 2.5%, may be NEGATIVE
 *   vols            normal (Bachelier), absolute, 0.0050 = 50bp/yr
 *   omega           +1 payer/call, -1 receiver/put
 *   notional        signed: + long (premium paid), - short (received)
 *   annuity         PV01 of the underlying swap per unit notional
 *   time            ACT/365 from the valuation date to the absolute expiry date
 *
 * The invariant that matters: NAV = cash + Σ(notional × mid) must be continuous
 * across every event. `checkNavIdentity` in analytics.ts asserts it.
 */

export const DAYS_PER_YEAR = 365.0;
export const MS_PER_DAY = 86400000;

export class BacktestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BacktestError";
  }
}

export class MissingMarketData extends BacktestError {
  constructor(message: string) {
    super(message);
    this.name = "MissingMarketData";
  }
}

export class PricingFailure extends BacktestError {
  constructor(message: string) {
    super(message);
    this.name = "PricingFailure";
  }
}

/**
 * Dates are handled as **UTC day numbers** (days since the epoch), not as Date
 * objects.
 *
 * This is deliberate. The previous JS port built `new Date("YYYY-MM-DD")` --
 * parsed as UTC midnight -- and then read `getFullYear()`/`getMonth()`, which
 * are local-time accessors. In any negative-UTC-offset browser the month label
 * shifted by a day and month-start rolls fired early, so a backtest gave
 * different answers in New York and in London. Integer day numbers cannot do
 * that, and they make year fractions and offsets exact integer arithmetic.
 */
export type DayNum = number;

export function dayNumFromISO(iso: string): DayNum {
  const [y, m, d] = iso.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / MS_PER_DAY);
}

export function isoFromDayNum(day: DayNum): string {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}

/** ACT/365 year fraction, floored at zero. Mirrors `core.year_fraction`. */
export function yearFraction(start: DayNum, end: DayNum): number {
  return Math.max(0, (end - start) / DAYS_PER_YEAR);
}

/** UTC calendar parts, safe in every timezone. */
export function utcParts(day: DayNum): { y: number; m: number; d: number } {
  const dt = new Date(day * MS_PER_DAY);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

/** ISO week number, UTC. Matches Python's `date.isocalendar()`. */
export function isoWeek(day: DayNum): { year: number; week: number } {
  const dt = new Date(day * MS_PER_DAY);
  const target = new Date(dt.getTime());
  const dayOfWeek = (dt.getUTCDay() + 6) % 7; // Monday = 0
  target.setUTCDate(target.getUTCDate() - dayOfWeek + 3); // nearest Thursday
  const isoYear = target.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const fdow = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - fdow + 3);
  const week =
    1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * MS_PER_DAY));
  return { year: isoYear, week };
}

// ===========================================================================
// Market data
// ===========================================================================

export interface SabrParams {
  alpha: number;
  beta: number;
  rho: number;
  nu: number;
  lam: number;
}

export interface RoughParams {
  H: number;
  rho: number;
  eta: number;
  atmVol: number;
}

/**
 * One (expiry, tenor) pillar on one valuation date.
 *
 * Two notions of time, and conflating them in either direction is a bug:
 *
 *   T       actual ACT/365 time to this pillar's adjusted expiry. The TERM
 *           AXIS: what variance accumulates over, and what a position's
 *           remaining life is compared against.
 *   tQuote  the year fraction the smile was CALIBRATED at (nominal n/12). The
 *           only thing that reproduces the quoted vols when fed to the smile
 *           formula.
 */
export interface SmileBucket {
  expiry: string;
  tenor: string;
  T: number;
  tQuote: number;
  expiryDay: DayNum;
  F: number;
  annuity: number;
  swapTenor: number;
  sabr: SabrParams | null;
  rough: RoughParams | null;
  volHalfSpread: number;
  priceHalfSpread: number;
}

export interface MarketSnapshot {
  day: DayNum;
  smiles: Map<string, SmileBucket>;
  byTenor: Map<string, SmileBucket[]>; // sorted ascending in T
  fundingRate: number;
  /** Per-tenor TermSlice cache, populated lazily by surface.ts. */
  slices: Map<string, unknown>;
}

export function bucketKey(expiry: string, tenor: string): string {
  return `${expiry}|${tenor}`;
}

export function makeSnapshot(
  day: DayNum,
  buckets: SmileBucket[],
  fundingRate = 0,
): MarketSnapshot {
  const smiles = new Map<string, SmileBucket>();
  const byTenor = new Map<string, SmileBucket[]>();
  for (const b of buckets) {
    smiles.set(bucketKey(b.expiry, b.tenor), b);
    const list = byTenor.get(b.tenor);
    if (list) list.push(b);
    else byTenor.set(b.tenor, [b]);
  }
  for (const list of byTenor.values()) list.sort((a, b) => a.T - b.T);
  return { day, smiles, byTenor, fundingRate, slices: new Map() };
}

export function getSmile(
  snap: MarketSnapshot,
  expiry: string,
  tenor: string,
): SmileBucket {
  const b = snap.smiles.get(bucketKey(expiry, tenor));
  if (!b) {
    throw new MissingMarketData(
      `no smile for ${expiry}x${tenor} on ${isoFromDayNum(snap.day)}`,
    );
  }
  return b;
}

export function tenorSlice(snap: MarketSnapshot, tenor: string): SmileBucket[] {
  const list = snap.byTenor.get(tenor);
  if (!list || list.length === 0) {
    throw new MissingMarketData(
      `no pillars for tenor=${tenor} on ${isoFromDayNum(snap.day)}`,
    );
  }
  return list;
}

export function tenors(snap: MarketSnapshot): string[] {
  return [...snap.byTenor.keys()].sort();
}

// ===========================================================================
// Positions
// ===========================================================================

export interface OptionLeg {
  legId: string;
  tenor: string;
  K: number;
  omega: 1 | -1;
  notional: number;
  openDay: DayNum;
  expiryDay: DayNum;
  openPrice: number;
  openVol: number;
  openF: number;
  openAnnuity: number;
  expiryLabel: string;
  tag: string;
  closeDay: DayNum | null;
  closePrice: number | null;
  closeVol: number | null;
  closeReason: "close" | "expiry" | null;
}

export function isOpen(leg: { closeDay: DayNum | null }): boolean {
  return leg.closeDay === null;
}

export function remainingT(leg: OptionLeg, day: DayNum): number {
  return yearFraction(day, leg.expiryDay);
}

export function isExpired(leg: OptionLeg, day: DayNum): boolean {
  return day >= leg.expiryDay;
}

/**
 * A forward-starting par swap used as a linear hedge.
 *
 * PV per unit notional is `annuity_t × (F_t − rate)` where F_t is today's
 * forward for THIS swap. Positive notional pays fixed, so it gains when rates
 * rise -- the same sign as an option's dV/dF, which is what makes delta
 * additive across option and swap legs.
 *
 * `startDay === null` means spot-starting, so `startT` is 0 forever.
 */
export interface SwapLeg {
  legId: string;
  tenor: string;
  notional: number;
  rate: number;
  openDay: DayNum;
  startDay: DayNum | null;
  tag: string;
  closeDay: DayNum | null;
  closeRate: number | null;
}

export function swapStartT(leg: SwapLeg, day: DayNum): number {
  return leg.startDay === null ? 0 : yearFraction(day, leg.startDay);
}

// ===========================================================================
// Orders
// ===========================================================================

export interface OpenOption {
  kind: "openOption";
  legId: string;
  expiry: string;
  tenor: string;
  K: number;
  omega: 1 | -1;
  notional: number;
  tag?: string;
  /** Absolute expiry, for the DSL's date form. Overrides the pillar lookup. */
  expiryDay?: DayNum;
}

export interface CloseOption {
  kind: "closeOption";
  legId: string;
}

/** Set a named swap hedge to an absolute notional. Cost is on |ΔN| only. */
export interface SetSwapHedge {
  kind: "setSwapHedge";
  legId: string;
  tenor: string;
  notional: number;
  startT?: number;
  tag?: string;
}

/**
 * Solve for the notional that brings portfolio delta to `target`.
 *
 * The solve EXCLUDES the hedge leg being adjusted. Sizing off the total, with
 * the old hedge still in the book, leaves a residual of exactly minus the old
 * hedge's delta -- which is what made the previous engine's residual delta
 * oscillate with a std of ~6 units instead of converging. Both the Python and
 * the earlier JS port had this bug.
 */
export interface TargetDelta {
  kind: "targetDelta";
  legId: string;
  tenor: string;
  target?: number;
  startT?: number;
  tag?: string;
}

export type Order = OpenOption | CloseOption | SetSwapHedge | TargetDelta;

// ===========================================================================
// Valuation result
// ===========================================================================

export interface LegValue {
  price: number;
  F: number;
  annuity: number;
  impliedVol: number;
  T: number;
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
}

export const GREEK_KEYS = ["delta", "gamma", "vega", "theta"] as const;
export type GreekKey = (typeof GREEK_KEYS)[number];
